import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Snackbar, Alert } from "@mui/material";

// A lightweight, stackable toast system built on MUI's Snackbar + Alert.
// No extra dependency is required because @mui/material already ships both.
//
// Usage:
//   const { showToast } = useToast();
//   showToast({ message: "Alice joined the room", variant: "info" });
//
// Each toast auto-dismisses after ~4s and can also be dismissed early via its
// close (X) button. Concurrent toasts stack rather than replace each other.

const ToastContext = createContext(null);

let toastId = 0;

const Toast = ({ id, variant, message, onClose }) => {
  return (
    <Snackbar
      open
      autoHideDuration={4000}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      onClose={onClose}
      key={id}
      sx={{
        mb: 1,
        "& .MuiAlert-root": {
          maxWidth: "90%",
          "&:focus": { outline: "none" },
        },
      }}
    >
      <Alert
        variant="filled"
        severity={variant}
        onClose={onClose}
        sx={{ minWidth: { xs: "80%", sm: "20rem", md: "24rem" } }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ message, variant = "info" }) => {
      const id = ++toastId;
      setToasts((prev) => [...prev, { id, message, variant }]);
      return id;
    },
    []
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((t) => (
        <Toast
          key={t.id}
          id={t.id}
          variant={t.variant}
          message={t.message}
          onClose={() => removeToast(t.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return { showToast: () => {} };
  }
  return ctx;
};

export default ToastProvider;
